/**
 * Persistence layer — localStorage autosave, the dice tray's roll history and
 * file import/export. UI → State → Domain → here; nothing else touches
 * storage.
 * TWO NAMESPACES, ONE SWITCH (`atla-gm:*` / `atla-player:*`).
 *
 * The reason is concrete: the GM screen-shares this app to Discord. A player
 * route (`#/play/*`) that could read the GM's campaign would leak prep notes
 * and NPC secrets the moment the GM broadcasts it — and a player link must be
 * safe to open on the GM's OWN machine, to test it or to show it on stream.
 * So every key below is built from ONE namespace constant (never a
 * hard-coded string): the GM cockpit under `atla-gm:`, the player shell under
 * `atla-player:`. The active namespace follows the route prefix at boot
 * (ui/router.ts), and a runtime crossing between surfaces FLUSHES the
 * outgoing autosave first, then swaps the namespace, clears the in-memory
 * campaign and loads the other one — so one surface's data is never shown
 * under the other's route. File export/import is namespace-free by
 * construction: a character file leaves one namespace as bytes and enters
 * the other as bytes (the whole point — the player exports, the GM imports).
 * The dice tray's roll history is the one key that is NOT a campaign: it lives
 * under the same namespace (`<namespace>:dice-history:v1`), never enters the
 * `.atlagm` envelope, and never crosses a surface either.
 */
import type { Campaign } from '@/domain/campaign';
import {
  CAMPAIGN_FILE_FORMAT,
  CAMPAIGN_FILE_VERSION,
  CampaignFileError,
  parseCampaignFile,
  serializeCampaignFile,
  type CampaignFile,
} from '@/domain/campaign-file';
import { TRAY_HISTORY_LIMIT, type EnvId, type TrayRoll } from '@/domain/dice-tray';
import { setSaveStatus, setToast, clearCampaign, replaceCampaign } from '@/state/store';

/** Which surface a route belongs to — derived from the route, never stored. */
export type StorageSurface = 'gm' | 'player' | 'cast';

/** The two local-storage namespaces. Every key starts with one of these. */
export const GM_NAMESPACE = 'atla-gm';
export const PLAYER_NAMESPACE = 'atla-player';
/**
 * The broadcast screen's own namespace (`#/cast`, the GM's second window).
 * It never holds a campaign of its own: the screen READS the GM's document
 * (`readGmCampaign`, read-only) and follows live edits over a
 * BroadcastChannel. Giving it a separate namespace is what guarantees the
 * second window can never write — and so never clobber — the GM's autosave.
 */
export const CAST_NAMESPACE = 'atla-cast';

/** Namespace of the surface currently in memory. */
let namespace: string = GM_NAMESPACE;

/** True while a surface crossing is mid-flight (see syncStorageSurface). */
let crossing = false;

const AUTOSAVE_DEBOUNCE_MS = 800;
const APP_VERSION = '0.1.0';

/** The active namespace — the gates assert the split through this. */
export function getStorageNamespace(): string {
  return namespace;
}

/** The one autosave key: built from the namespace, never hard-coded. */
function autosaveKey(): string {
  return `${namespace}:autosave:v1`;
}

function namespaceOf(surface: StorageSurface): string {
  return surface === 'player' ? PLAYER_NAMESPACE : surface === 'cast' ? CAST_NAMESPACE : GM_NAMESPACE;
}

/**
 * READ-ONLY view of the GM's autosaved campaign, for the broadcast screen's
 * first paint (live edits then arrive over the cast channel). Never writes,
 * never touches the active namespace; a missing or damaged save reads as null.
 */
export function readGmCampaign(): Campaign | null {
  try {
    const raw = localStorage.getItem(`${GM_NAMESPACE}:autosave:v1`);
    if (!raw) return null;
    return parseCampaignFile(JSON.parse(raw)).campaign;
  } catch {
    return null;
  }
}

/**
 * Point the persistence layer at a surface. Idempotent; called on boot
 * (before the first read, so the route prefix picks the namespace) and on
 * every runtime crossing (a player link followed inside the running app).
 *
 * On a REAL crossing the outgoing autosave is flushed FIRST — still under
 * the outgoing namespace — then the namespace flips, the in-memory campaign
 * is cleared and the other namespace's autosave is loaded. Without that
 * order the outgoing edits would be written under (or worse, restored from)
 * the wrong surface's key, and the campaign on screen could belong to the
 * route's neighbour.
 */
export function syncStorageSurface(surface: StorageSurface): void {
  const next = namespaceOf(surface);
  // REENTRANCY GUARD — the crossing must be atomic. flush/replace notify the
  // store, and a subscription (the hash router's onStoreChange) calls straight
  // back into this function WHILE `namespace` still holds the outgoing value;
  // without the guard that re-entry restarts the crossing, which notifies
  // again — an endless flush loop that overflows the stack, gets caught by
  // flushAutosave's try/catch and starts over (the page would freeze on the
  // first surface switch). A nested call now returns immediately; every
  // notification issued AFTER the flip finds the namespaces equal and is the
  // documented no-op anyway.
  if (crossing) return;
  // NOTHING TO CROSS. This early return is what makes the call a no-op when the surface did not
  // change, and without it the function re-runs its whole flush/clear/load cycle on EVERY store
  // notification -- including the ones its own replaceCampaign() issues. The router's onStoreChange
  // calls straight back in, so with a campaign in localStorage the page locks up at boot (a frozen
  // render loop, which is what the browser reports as "out of memory"). A browser with an EMPTY
  // profile hides the bug completely, because loadAutosave() finds nothing, replaceCampaign() never
  // notifies, and the cycle starves -- which is why every empty-profile test passed while the GM's
  // own browser hung the moment real saved data existed.
  if (next === namespace) return;
  crossing = true;
  try {
    flushAutosave(); // outgoing data, outgoing key — BEFORE the flip
    namespace = next;
    clearCampaign(); // drop the other surface's document from memory
    const restored = loadAutosave(); // the new namespace's document
    if (restored) replaceCampaign(restored.campaign);
  } finally {
    crossing = false;
  }
}

// ----------------------------------------------------------------- autosave

let timer: ReturnType<typeof setTimeout> | null = null;

/** Debounced save of the current campaign; subtle status via the store. */
export function scheduleAutosave(): void {
  setSaveStatus('saving');
  if (timer !== null) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    try {
      const file = buildFile();
      if (file) {
        localStorage.setItem(autosaveKey(), serializeCampaignFile(file));
        setSaveStatus('saved', new Date().toISOString());
      } else {
        setSaveStatus('idle');
      }
    } catch {
      setSaveStatus('error');
    }
  }, AUTOSAVE_DEBOUNCE_MS);
}

export function flushAutosave(): void {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  try {
    const file = buildFile();
    if (file) {
      localStorage.setItem(autosaveKey(), serializeCampaignFile(file));
      setSaveStatus('saved', new Date().toISOString());
    }
  } catch {
    setSaveStatus('error');
  }
}

let getCampaign: (() => Campaign | null) | null = null;

/** The store wires this in (avoids a state↔persistence import cycle). */
export function bindAutosaveSource(source: () => Campaign | null): void {
  getCampaign = source;
}

function buildFile(): CampaignFile | null {
  const campaign = getCampaign?.() ?? null;
  if (!campaign) return null;
  return {
    format: CAMPAIGN_FILE_FORMAT,
    version: CAMPAIGN_FILE_VERSION,
    exportedAt: new Date().toISOString(),
    appVersion: APP_VERSION,
    campaign,
  };
}

/**
 * Read the autosave. A damaged value must never throw out of boot (the app
 * would then crash on EVERY load): it returns "no saved state" and clears the
 * key so the next boot is clean. A state that parses is returned untouched —
 * nothing parseable is ever discarded here.
 */
export function loadAutosave(): CampaignFile | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(autosaveKey());
  } catch {
    return null; // storage itself unavailable (privacy mode) — not a damaged save
  }
  if (!raw) return null;
  try {
    return parseCampaignFile(JSON.parse(raw));
  } catch {
    try {
      localStorage.removeItem(autosaveKey());
    } catch {
      // best effort — without storage there is nothing more we can do
    }
    setToast('save.corrupted');
    return null;
  }
}

export function clearAutosave(): void {
  localStorage.removeItem(autosaveKey());
}

// ----------------------------------------------------------- dice history

/**
 * The header tray's roll history (`<namespace>:dice-history:v1`), so a roll
 * outlives the tab it was rolled in. It is NOT campaign data — it is never
 * written into the `.atlagm` envelope and it never travels between surfaces:
 * the key is built from the SAME namespace constant as the autosave, so the
 * GM's history cannot show up under a player link (the reason the split
 * exists in the first place).
 */
function diceHistoryKey(): string {
  return `${namespace}:dice-history:v1`;
}

/** The three chips, as the one list the tray may persist (see domain). */
const TRAY_ENV_IDS: readonly EnvId[] = ['full-moon', 'comet', 'eclipse'];

/** Shape check on the way IN — one corrupt entry must not break the render. */
function isTrayRoll(value: unknown): value is TrayRoll {
  if (typeof value !== 'object' || value === null) return false;
  const roll = value as Record<string, unknown>;
  return (
    typeof roll.id === 'number' &&
    (roll.mode === 'fate' || roll.mode === 'dnd') &&
    typeof roll.notation === 'string' &&
    typeof roll.line === 'string' &&
    typeof roll.total === 'number' &&
    (roll.tone === 'crit' || roll.tone === 'fumble' || roll.tone === 'normal') &&
    (roll.env === null ||
      (typeof roll.env === 'string' && TRAY_ENV_IDS.includes(roll.env as EnvId))) &&
    typeof roll.at === 'number'
  );
}

/**
 * Read the tray history. Storage being unavailable (private mode) reads as
 * "no rolls" rather than throwing — the popover must open either way. A
 * damaged value clears its key, exactly like the autosave does, so the next
 * boot starts clean instead of failing forever.
 */
export function loadDiceHistory(): TrayRoll[] {
  let raw: string | null;
  try {
    raw = localStorage.getItem(diceHistoryKey());
  } catch {
    return []; // storage itself unavailable — not a damaged history
  }
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) throw new Error('dice history is not an array');
    return parsed.filter(isTrayRoll).slice(0, TRAY_HISTORY_LIMIT);
  } catch {
    try {
      localStorage.removeItem(diceHistoryKey());
    } catch {
      // best effort — without storage there is nothing more we can do
    }
    return [];
  }
}

/** Persist the tray history. Never throws: a full disk only costs the log. */
export function saveDiceHistory(history: readonly TrayRoll[]): void {
  try {
    // Clearing the tray clears the key too — an empty array is not worth a slot.
    if (history.length === 0) {
      localStorage.removeItem(diceHistoryKey());
      return;
    }
    localStorage.setItem(
      diceHistoryKey(),
      JSON.stringify(history.slice(0, TRAY_HISTORY_LIMIT)),
    );
  } catch {
    // quota exceeded / storage unavailable — the tray keeps working
  }
}

// -------------------------------------------------------------------- file

/** Export the active campaign as `my-atla-campaign.atlagm` (§A-10). */
export function exportCampaignFile(campaign: Campaign): void {
  const file: CampaignFile = {
    format: CAMPAIGN_FILE_FORMAT,
    version: CAMPAIGN_FILE_VERSION,
    exportedAt: new Date().toISOString(),
    appVersion: APP_VERSION,
    campaign,
  };
  const blob = new Blob([serializeCampaignFile(file)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const slug =
    campaign.name.trim().toLowerCase().replace(/[^a-z0-9ğüşıöç]+/gi, '-').replace(/^-|-$/g, '') ||
    'my-atla-campaign';
  a.href = url;
  a.download = `${slug}.atlagm`;
  a.click();
  URL.revokeObjectURL(url);
  setToast('export.done');
}

/**
 * Surface a failed .atlagm read in the user's language: a CampaignFileError
 * carries the i18n key that names the exact problem plus the offending field
 * ({field}); anything else (network failure, JSON syntax) falls back to
 * `fallbackKey`. Shared by the import button and the sample loaders.
 */
export function showImportError(
  err: unknown,
  fallbackKey = 'import.error.invalid',
): void {
  if (err instanceof CampaignFileError) {
    setToast(err.key, err.field !== undefined ? { field: err.field } : null);
  } else {
    setToast(fallbackKey);
  }
}

/** Read + validate a chosen .atlagm/.json file; adopts it into the store. */
export async function importCampaignFile(file: File): Promise<boolean> {
  try {
    const text = await file.text();
    const parsed: unknown = JSON.parse(text);
    const campaignFile = parseCampaignFile(parsed);
    replaceCampaign(campaignFile.campaign);
    setToast('import.done');
    flushAutosave();
    return true;
  } catch (err) {
    showImportError(err);
    return false;
  }
}
